import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import LandingPage from "@/pages/landing";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

const MODAL_KEY = "structspectrum_modal_seen";
const REFERRAL_KEY = "referral_source";
const PARTNER = "structspectrum";

export default function StructSpectrumPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(REFERRAL_KEY, PARTNER);
    if (!localStorage.getItem(MODAL_KEY)) {
      setModalOpen(true);
    }
  }, []);

  const dismissModal = () => {
    localStorage.setItem(MODAL_KEY, "1");
    setModalOpen(false);
  };

  if (user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <LandingPage />
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) dismissModal(); }}>
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-lg">🏗</span>
              </div>
              <div>
                <DialogTitle className="text-lg font-bold leading-tight">Welcome from StructSpectrum</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">Partnership offer: please read before signing up</DialogDescription>
              </div>
            </div>

            <p className="text-sm leading-relaxed">
              The Proper Tool is proud to partner with <strong>StructSpectrum</strong>, a UK structural engineering firm.
            </p>

            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <p className="text-sm font-semibold text-primary mb-1">Your exclusive offer:</p>
              <p className="text-sm leading-relaxed">
                As a StructSpectrum referral, you are eligible for <strong>50% off your next structural calculation</strong> when you subscribe to any paid plan on The Proper Tool.
              </p>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold">Discount caps by subscription tier:</p>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between border-b pb-1">
                  <span className="text-muted-foreground">Lite (£29/month)</span>
                  <span className="font-medium">50% off, up to £200</span>
                </div>
                <div className="flex justify-between border-b pb-1">
                  <span className="text-muted-foreground">Starter (£59/month)</span>
                  <span className="font-medium">50% off, up to £300</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pro (£99/month)</span>
                  <span className="font-medium">50% off, up to £500</span>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-semibold">Terms:</p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Free trial period does not qualify for this discount</li>
                <li>Discount activates only when you subscribe to a paid plan</li>
                <li>Limited to one calculation per account</li>
                <li>Applies to your first calculation through StructSpectrum after subscribing</li>
                <li>Discount code will be visible in your account settings once you subscribe</li>
                <li>Cancellation of subscription does not retroactively void a used discount</li>
                <li>Discount cap is set at the time the code is generated and does not change with upgrades or downgrades</li>
              </ul>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-semibold">Explore The Proper Tool:</p>
              <ul className="text-sm space-y-1">
                <li>
                  <a href="/auto-quote" className="text-primary hover:underline">Auto Quote</a>
                  {" - describe a job, get a priced quote in minutes"}
                </li>
                <li>
                  <a href="/quote-from-drawings" className="text-primary hover:underline">Quote from Drawings</a>
                  {" - upload plans or PDFs for AI takeoff"}
                </li>
                <li>
                  <a href="/best/quoting-software-for-uk-builders" className="text-primary hover:underline">Compare all UK quoting tools</a>
                  {" - see how we stack up against the alternatives"}
                </li>
                <li>
                  <a href="/pricing" className="text-primary hover:underline">Pricing</a>
                  {" - plans from £29/month, 14-day free trial"}
                </li>
              </ul>
            </div>

            <Button className="w-full" onClick={dismissModal} data-testid="button-structspectrum-dismiss">
              Got it, let's go
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
